import React from 'react';
import { GripVertical } from 'lucide-react';
import type { AdminFlowQuestion } from '../types';

interface QuestionPreviewCardProps {
  q: AdminFlowQuestion;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

const QuestionPreviewCard: React.FC<QuestionPreviewCardProps> = ({ q, selected, onClick, onDelete, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all bg-white dark:bg-gray-800 shadow-sm hover:shadow-md ${
        isDragOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 scale-[0.99]' :
        selected ? 'border-blue-500 shadow-blue-100 dark:shadow-blue-900/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      {/* Drag handle */}
      <div className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing" onClick={e => e.stopPropagation()}>
        <GripVertical size={14} />
      </div>
      {/* Required badge */}
      <div className="absolute top-3 left-8">
        {q.required ? (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400" title="必填" />
        ) : (
          <span className="text-[10px] text-gray-400 dark:text-gray-500">選填</span>
        )}
      </div>

      {/* Delete button */}
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        className="absolute top-2.5 right-2.5 text-gray-300 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400 text-sm transition-colors"
        title="刪除此題"
      >
        🗑
      </button>

      {/* Title */}
      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mt-3 mb-3 pr-6">
        {q.title || <span className="text-gray-400 dark:text-gray-500 italic">（未命名）</span>}
        {q.required && <span className="text-red-400 ml-0.5">*</span>}
      </p>

      {/* Type badge */}
      <span className="absolute top-3 right-8 text-[10px] text-gray-400 dark:text-gray-500 font-mono">{q.type}</span>

      {/* Preview by type */}
      {q.type === 'select' && (
        <div className="flex flex-wrap gap-1.5">
          {(q.options ?? []).length > 0 ? (q.options ?? []).map((o, i) => (
            <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600">
              {o.label || o.value}
            </span>
          )) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">尚無選項</span>
          )}
        </div>
      )}

      {q.type === 'text' && (
        <div className="bg-gray-100 dark:bg-gray-700/60 rounded-lg px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
          在此輸入文字...
        </div>
      )}

      {q.type === 'boolean' && (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center">
            <div className="w-2 h-2 rounded-sm bg-gray-300 dark:bg-gray-600" />
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500">是 / 否</span>
        </div>
      )}

      {q.type === 'image' && (
        <div className="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 text-center text-xs text-gray-400 dark:text-gray-500">
          點擊或拖曳上傳圖片
        </div>
      )}
    </div>
  );
};

export default QuestionPreviewCard;
